import { type FC, useEffect } from "react";
import { BrowserRouter, Redirect, Route, Switch, useLocation, useRouteMatch } from "react-router-dom";

import "./App.scss";
import { Bullseye } from "@patternfly/react-core";
import { AlertComponent, AlertProvider } from "@rhc-shared-components/alert-component";
import { ControllerContextProvider } from "@rhc-shared-components/control-wrapper";
import { RHCErrorPage } from "@rhc-shared-components/error-page";
import { checkAndSendAnalytics } from "@rhc-shared-components/rhc-eddl";
import { RHCAuthProvider, useRHCAuthContext } from "@rhc-shared-components/rhc-sso";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getPlsConfig } from "./api/hydra";
import ComponentLoader, { EComponentList } from "./components/general/ComponentLoader/ComponentLoader";
import Spinner from "./components/general/Spinner/Spinner";
import ProductDetail from "./components/ProductDetailsWrapper/ProductDetailsWrapper";

import ProductListWrapper from "./components/ProductList/ProductListWrapper";
import type { ProductRouteParams } from "./constants/routerconstants";
import { useOnboardingStatus } from "./queries/general";
import Env from "./utils/env";
import { getUserInfoFromJWT } from "./utils/fetch";
import { Logger } from "./utils/logger";
import { RedirectToOnboardingPage } from "./utils/redirect";

declare global {
	interface Window {
		__TANSTACK_QUERY_CLIENT__: import("@tanstack/query-core").QueryClient;
	}
}

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			retry: 2,
			refetchOnWindowFocus: false,
			staleTime: 10 * 60 * 1000, // 10 Minutes
		},
	},
});

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

const ProductDetailsWrapper = (props: Record<string, unknown>) => <ProductDetail {...props} />;

const ProjectList = ({ match }: { match: import("react-router").match }) => (
	<ComponentLoader componentName={EComponentList.ProjectList} match={match} />
);

const RedirectComponent: FC = () => {
	const match = useRouteMatch<ProductRouteParams>();
	const productId = match?.params.productId;

	const path = match?.path.split("/").pop() || "";
	const prefix = `${window.location.origin}${Env.routePrefix}/products/${productId}`;
	window.location.href = `${prefix}/${path !== "certification-projects" ? `product-information/${path}` : "components"}`;
	return (
		<div
			style={{
				minHeight: "100%",
			}}
		>
			<Bullseye>Redirecting to the new url, please update your bookmarks...</Bullseye>
		</div>
	);
};

const AuthenticatedApp: FC = () => {
	const { isAuthenticated } = useRHCAuthContext();
	const { pathname } = useLocation();
	const { data: onboardingStatus, isLoading, error } = useOnboardingStatus(isAuthenticated);

	useEffect(() => {
		if (!onboardingStatus) return;

		Logger("User is authenticated");
		if (onboardingStatus && onboardingStatus.status === "completed") {
			Logger("User is onboarded, enabling the app");
		} else {
			Logger("User is not onboarded, redirecting to tnc page");
			RedirectToOnboardingPage();
		}
	}, [onboardingStatus]);

	useEffect(() => {
		if (isAuthenticated && Env.getEnvName() === Env.EnvNames.PROD) {
			Sentry.setUser(getUserInfoFromJWT());
		}
	}, [isAuthenticated]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pathname triggers analytics on route change
	useEffect(() => {
		if (isAuthenticated) {
			checkAndSendAnalytics({ spaName: "product-listing" });
		}
	}, [isAuthenticated, pathname]);

	if (error) {
		return (
			<div role="main" className="app">
				<RHCErrorPage errorStatus={error?.status} />
			</div>
		);
	}
	if (isLoading || !isAuthenticated) {
		return <Spinner />;
	}
	return (
		<ControllerContextProvider appKey={"pls_config"} configApi={getPlsConfig} bannerClassName="rhc-center">
			<span className="main-app">
				<AlertProvider>
					<AlertComponent />
					<Switch>
						<Redirect from="/" to={`${Env.routePrefix}/products`} exact />
						<Redirect from={Env.routePrefix} to={`${Env.routePrefix}/products`} exact />
						<Redirect from={Env.routePrefix} to={`${Env.routePrefix}/products`} exact />
						<Route exact={true} path={`${Env.routePrefix}/products`} component={ProductListWrapper} />
						<Redirect from={`${Env.routePrefix}/projects`} to={`${Env.routePrefix}/components`} exact />
						<Route exact={true} path={`${Env.routePrefix}/components`} component={ProjectList} />
						<Route exact={true} path={`${Env.routePrefix}/products/:productId/general`} component={RedirectComponent} />
						<Route
							exact={true}
							path={`${Env.routePrefix}/products/:productId/features`}
							component={RedirectComponent}
						/>
						<Route
							exact={true}
							path={`${Env.routePrefix}/products/:productId/quick-start`}
							component={RedirectComponent}
						/>
						<Route
							exact={true}
							path={`${Env.routePrefix}/products/:productId/linked-resources`}
							component={RedirectComponent}
						/>
						<Route exact={true} path={`${Env.routePrefix}/products/:productId/faq`} component={RedirectComponent} />
						<Route exact={true} path={`${Env.routePrefix}/products/:productId/legal`} component={RedirectComponent} />
						<Route
							exact={true}
							path={`${Env.routePrefix}/products/:productId/version-matrix`}
							component={RedirectComponent}
						/>
						<Route exact={true} path={`${Env.routePrefix}/products/:productId/seo`} component={RedirectComponent} />
						<Route
							exact={true}
							path={`${Env.routePrefix}/products/:productId/certification-projects`}
							component={RedirectComponent}
						/>
						<Route path={`${Env.routePrefix}/products/:productId`} component={ProductDetailsWrapper} />
						<Route render={() => <RHCErrorPage errorStatus={404} />} />
					</Switch>
					<div id="modal-div-container" />
				</AlertProvider>
			</span>
		</ControllerContextProvider>
	);
};

const App: FC = () => (
	<QueryClientProvider client={queryClient}>
		<RHCAuthProvider>
			<BrowserRouter>
				<div role="main" className="app">
					<AuthenticatedApp />
				</div>
			</BrowserRouter>
		</RHCAuthProvider>
	</QueryClientProvider>
);

export default App;
